/**
 * Which registries a number is checked against — the one grouping the person hub makes on this
 * side (SR-215; the participant card of SR-213 block 8 before it) — which numbers the hub can ask
 * a registry about again — and as which type the registry is asked (SR-213's Teams block) — and
 * which survey-api looks up in the SEC ADV register for a participant's register firms (SR-214);
 * and, beside those four mirrors, the one set here that mirrors nothing: which numbers the hub
 * offers "Verify number" on (SR-215) — and one grouping of answers by key that is words and
 * not a mirror either: which answers are about somebody else's number (`READING_OF_FACT_KEY`,
 * SR-416).
 *
 * survey-api declined to send the first (its advisor-profile plan, R5): since roadmap D3 an
 * identifier type already says whose number it is, and the registry each type belongs to
 * lives in its fact providers and `RegistrationSurveys`. A `registry` member on the wire would
 * have been a third copy of that mapping. This table is the second, and has to move with the
 * first — the `COLUMN_DEFAULTS` precedent. **A type maps to a list**, since SR-396 asks the SEC's
 * IAPD beside FINRA's BrokerCheck about every person: `IapdFactKeys.PairedWith` pairs
 * `brokercheck.person` with `iapd.person` (and a roster member's `brokercheck.member` with
 * `iapd.member`), and nothing else — firms are the ADV register's. It mirrors:
 *
 * - `CRD` → `brokercheck` (`brokercheck.person`) and `iapd` (`iapd.person`), and `ORG_CRD` →
 *   `brokercheck` (`brokercheck.firm`) alone — FA's registration and `RegistryLookupService`'s
 *   refresh;
 * - `TEAM_LEAD_CRD` → `brokercheck` and `iapd` (`*.person`) — TEAMS' registration, which asks
 *   about a team's lead advisor as FA asks about an advisor;
 * - `SEC_NUMBER` → `sec_adv` (`adv.firm_profile`) — RIA_FIRM's registration;
 * - `NPN` → `nipr` (`nipr.person`) — FSP's registration.
 *
 * Keyed by survey-api's `sourceSystem` codes, which is what a registry's answer carries — so a
 * group holds its numbers and what that registry last said, side by side; a CRD sits in both
 * BrokerCheck's group and IAPD's, each saying what its own registry answered.
 *
 * **Nothing is dropped.** A type this table does not know — `EMAIL`, a migrated
 * `LEGACY_USER_ID`, `INSURANCE_LICENSE`, anything survey-api adds later — names no registry (on
 * the hub it is an internal id, or a registry number whose registry reads "None"), and an answer
 * from a source it does not know — `prior_cycle.summary`, anything new — goes under "Other
 * registry answers", rather than out of sight. The hub is where staff look
 * for what the product holds about somebody, and a thing missing from it would be the one
 * thing it got wrong.
 */

/** The registries survey-api asks about numbers, by `sourceSystem`, in the hub's order. */
const REGISTRIES = [
  { sourceSystem: 'brokercheck', label: 'FINRA BrokerCheck' },
  { sourceSystem: 'iapd', label: 'SEC IAPD' },
  { sourceSystem: 'sec_adv', label: 'SEC Form ADV' },
  { sourceSystem: 'nipr', label: 'NIPR' }
] as const;

export type RegistryCode = (typeof REGISTRIES)[number]['sourceSystem'];

/** A map rather than an object literal, so that a type spelled like an `Object.prototype`
 *  member reads as unknown instead of finding a function. Each list in `REGISTRIES`' order. */
export const REGISTRY_OF_ID_TYPE = new Map<string, readonly RegistryCode[]>([
  ['CRD', ['brokercheck', 'iapd']],
  ['ORG_CRD', ['brokercheck']],
  ['TEAM_LEAD_CRD', ['brokercheck', 'iapd']],
  ['SEC_NUMBER', ['sec_adv']],
  ['NPN', ['nipr']]
]) as ReadonlyMap<string, readonly RegistryCode[]>;

/** The registries a number of this type is checked against — none for a type the table lacks. */
function registriesOf(idType: string): readonly string[] {
  return REGISTRY_OF_ID_TYPE.get(idType) ?? [];
}

/**
 * The numbers `POST …/registry-lookups` asks a registry about again, and the name the button
 * gives what it asks — a mirror of `RegistryLookupService.Refreshes` in survey-api (E3), which
 * covers `CRD`, `ORG_CRD` and, since its Teams block, `TEAM_LEAD_CRD` — all BrokerCheck, with IAPD
 * asked beside it on a person's number since SR-396 (the pair, `IapdFactKeys.PairedWith`; a firm
 * CRD is BrokerCheck's alone) — and nothing else: SEC ADV is a register the worker imports and
 * NIPR is still a stub. The names are the owner's (SR-396's Q1): "Ask BrokerCheck and IAPD again".
 *
 * It has to move with that dictionary, the `COLUMN_DEFAULTS` precedent. Drifting one way, the
 * hub offers a button survey-api answers 422 for — which the hub still renders, as survey-api
 * no longer refreshing that type; drifting the other, a refresh survey-api offers is never
 * offered here. Asking survey-api instead was the alternative, and it has no route that says.
 */
export const REFRESH_OF_ID_TYPE = new Map<string, string>([
  ['CRD', 'BrokerCheck and IAPD'],
  ['ORG_CRD', 'BrokerCheck'],
  ['TEAM_LEAD_CRD', 'BrokerCheck and IAPD']
]) as ReadonlyMap<string, string>;

/**
 * The type a registry is asked a number **as**, where it is not the type the number is held
 * as — a mirror of `Refresh.AskAs` in survey-api's `RegistryLookupService.Refreshes`, and of
 * `RegistrationSurveys`' TEAMS entry, which asks FINRA about the lead the same way. A team holds
 * its lead advisor's CRD as `TEAM_LEAD_CRD`, and FINRA is asked about the lead as a person: so
 * the lookup a press or a registration enqueues, and the pending lookup the hub is sent, name
 * `CRD` and the lead CRD's value — tied to the number by value, by design (its B3).
 *
 * The hub matches a lookup to a number on the pair (`asksAbout`), so the pair is read through
 * this table: without it a lead CRD's own lookup would be drawn as waiting on no number held,
 * its button would not be held while it waits, and a press answered about it would read as a
 * lookup about another number. Matching on the value alone was the alternative, and would pair
 * a lookup with any number of that value whatever its type. It has to move with that
 * dictionary, the `REFRESH_OF_ID_TYPE` precedent.
 */
const ASKED_AS_OF_ID_TYPE = new Map<string, string>([
  ['TEAM_LEAD_CRD', 'CRD']
]) as ReadonlyMap<string, string>;

/** The type a registry is asked a number of this type as — its own, unless
 *  `ASKED_AS_OF_ID_TYPE` says otherwise. */
export function askedAs(idType: string): string {
  return ASKED_AS_OF_ID_TYPE.get(idType) ?? idType;
}

/**
 * The numbers survey-api looks the participant up in the SEC ADV register by, for the
 * participant document's `secAdvFirms` — a mirror of `EfAdminParticipantDetailQuery.SecAdvFirmsAsync` in survey-api
 * (its SR-214): a `SEC_NUMBER` matched against a firm's SEC number, an `ORG_CRD` against its firm
 * CRD, and no other type.
 *
 * The hub needs it to say that the register was asked and named nobody: its SEC ADV register
 * group is drawn when survey-api names a firm, and otherwise only for a participant holding a
 * number of one of these types — every other participant was never looked up, and "no register
 * firm has these numbers" would say something of numbers nobody compared. No route says which
 * types survey-api matches, so this has to move with that query, the `REFRESH_OF_ID_TYPE`
 * precedent. Drifting one way, the hub says of a number the register was never asked about that
 * it names no firm; the other, it stays silent where the register was asked and named none. A
 * firm survey-api does name is drawn whatever this set holds: the list is the server's.
 */
export const SEC_ADV_MATCHED_ID_TYPES: ReadonlySet<string> = new Set(['SEC_NUMBER', 'ORG_CRD']);

/**
 * The numbers the hub draws as **registry numbers** and offers "Verify number" on; every other
 * type is an internal id, drawn without a button.
 *
 * **This is not a mirror, and must not be "fixed" into one.** survey-api's
 * `…/identifier-verifications` stamps a number of any type, and says nothing about which ones a
 * person should verify. That staff verify registry numbers only is the product's rule (the
 * person hub assessment's ruling 7, the owner's): an `EMAIL` or a migrated `LEGACY_USER_ID` is
 * this system's own record, and "verified" on one would claim a check nobody can make.
 * `INSURANCE_LICENSE` is here although no registry above answers for it — a state regulator
 * issues it, and staff check it by hand — which is why the hub offers Verify and no "Ask …
 * again" on it. `TEAM_LEAD_CRD` is here by the owner's ruling on the Teams plan's Q4: a restored
 * team's lead CRD comes back unverified, and staff verify it again. A type added here or dropped
 * from here is a product decision, not a sync.
 */
export const VERIFIABLE_ID_TYPES: ReadonlySet<string> = new Set([
  'CRD',
  'ORG_CRD',
  'TEAM_LEAD_CRD',
  'NPN',
  'SEC_NUMBER',
  'INSURANCE_LICENSE'
]);

/** The registry numbers among a participant's identifiers (`VERIFIABLE_ID_TYPES`), in the order
 *  sent — the ones the hub and People draw as chips and in the Registry numbers table. */
export function registryNumbers<N extends { idType: string }>(identifiers: N[]): N[] {
  return identifiers.filter((identifier) => VERIFIABLE_ID_TYPES.has(identifier.idType));
}

/** The registries a number is checked against, by the names the hub gives them, in
 *  `REGISTRIES`' order ("FINRA BrokerCheck, SEC IAPD") — null for a type no registry above
 *  answers for (`INSURANCE_LICENSE`, an internal id). */
export function registryLabel(idType: string): Nullable<string> {
  const registries = registriesOf(idType);
  const named = REGISTRIES.filter(({ sourceSystem }) => registries.includes(sourceSystem));
  return named.length > 0 ? named.map(({ label }) => label).join(', ') : null;
}

/**
 * The answers survey-api keeps under a registry's source, or under its own `veritant` source, that
 * are **not about a number the participant holds** (SR-416), by `factKey` — each read under a
 * group of its own rather than its source's, and never as a number's "Last answer".
 *
 * **Words, not a mirror.** survey-api says what each key is in its C# (`RespondentFactKeys`,
 * `U4Disclosures`, `FirmListMatcher`, `BrokerCheckRefreshFactKeys.Member`, `IapdFactKeys.Member`)
 * and on the wire sends a fact's source and key and nothing else — **a fact names no number**:
 * the hub serves the newest fact per `(sourceSystem, factKey)`. So which keys belong together is
 * this app's grouping of what it draws, like the registries' labels above, and a key survey-api
 * adds later is not dropped: one under a registry's source stays in that registry's group, one
 * under `veritant` goes under "Other registry answers".
 *
 * - `veritant` — **Veritant's readings** of the participant's own person answers:
 *   `firm.list_match.wirehouse` (whether a current firm is on the wirehouse list) and
 *   `u4.disclosures` (whether the registries hold disclosures), refreshed at a response's
 *   create, a reopen and the worker's answer to a person lookup;
 * - `member` — **a team's or firm's members looked up** from inside a response (and by the
 *   scheduled refresh, for `*.member`): each payload names its member (`member: {crd, slot}`),
 *   and the hub serves the newest of each key, whichever member it is about;
 * - `respondent` — **the CRD a respondent typed into the FSP survey** (`FSP-RG-003`), asked of
 *   both registries under keys of their own so it never becomes the participant's number:
 *   FINRA's and IAPD's envelopes name no CRD, `u4.respondent`'s payload does.
 */
export type FactReading = 'veritant' | 'member' | 'respondent';

/** The groups those keys are read under, in the hub's order — after the registries'. */
const FACT_READINGS: readonly { reading: FactReading; label: string }[] = [
  { reading: 'veritant', label: 'Veritant’s readings' },
  { reading: 'member', label: 'Team members looked up' },
  { reading: 'respondent', label: 'The CRD typed into the FSP survey' }
];

export const READING_OF_FACT_KEY = new Map<string, FactReading>([
  ['firm.list_match.wirehouse', 'veritant'],
  ['u4.disclosures', 'veritant'],
  ['brokercheck.member', 'member'],
  ['iapd.member', 'member'],
  ['u4.member', 'member'],
  ['brokercheck.respondent', 'respondent'],
  ['iapd.respondent', 'respondent'],
  ['u4.respondent', 'respondent']
]) as ReadonlyMap<string, FactReading>;

/**
 * What the registries a number is checked against last answered for its participant — every
 * answer of their `sourceSystem`s, as `groupByRegistry` groups them, so a CRD and a firm CRD both
 * show BrokerCheck's person and firm answers, and a CRD IAPD's beside them. Matched by source
 * rather than by an idType → `factKey` table, which would be survey-api's fact providers
 * transcribed a second time — **less the answers about somebody else's number**
 * (`READING_OF_FACT_KEY`: a member's, the CRD typed into the FSP survey), so a CRD's "Last
 * answer" is the participant's own.
 */
export function answersFor<F extends { sourceSystem: string; factKey: string }>(
  idType: string,
  facts: F[]
): F[] {
  const registries = registriesOf(idType);
  return facts.filter(
    (fact) => registries.includes(fact.sourceSystem) && !READING_OF_FACT_KEY.has(fact.factKey)
  );
}

export interface IRegistryGroup<N, F> {
  /** Null for the groups no registry above claims: the readings, other identifiers, other
   *  answers. */
  registry: Nullable<RegistryCode>;
  /** Set for a group of `READING_OF_FACT_KEY`'s, null for every other. */
  reading: Nullable<FactReading>;
  label: string;
  numbers: N[];
  /** What this registry last answered, per fact key — survey-api's newest per
   *  `(sourceSystem, factKey)`. */
  facts: F[];
}

/**
 * Numbers and registry answers by the registry they belong to — the registries in the order
 * above, then the readings (`READING_OF_FACT_KEY`, by key before source), then the numbers of
 * any other type, then the answers from any other source. Only groups that hold something, in
 * the order survey-api sent them. Every answer is in exactly one group; **a number is in every
 * group of a registry it is checked against** (a CRD under BrokerCheck and IAPD), so a registry
 * that has not answered about it yet still says so.
 */
export function groupByRegistry<
  N extends { idType: string },
  F extends { sourceSystem: string; factKey: string }
>(numbers: N[], facts: F[]): IRegistryGroup<N, F>[] {
  const claimed = new Set<string>(REGISTRIES.map(({ sourceSystem }) => sourceSystem));
  const unread = facts.filter((fact) => !READING_OF_FACT_KEY.has(fact.factKey));
  const known: IRegistryGroup<N, F>[] = REGISTRIES.map(({ sourceSystem, label }) => ({
    registry: sourceSystem,
    reading: null,
    label,
    numbers: numbers.filter((number) => registriesOf(number.idType).includes(sourceSystem)),
    facts: unread.filter((fact) => fact.sourceSystem === sourceSystem)
  }));
  const readings: IRegistryGroup<N, F>[] = FACT_READINGS.map(({ reading, label }) => ({
    registry: null,
    reading,
    label,
    numbers: [],
    facts: facts.filter((fact) => READING_OF_FACT_KEY.get(fact.factKey) === reading)
  }));
  const otherNumbers: IRegistryGroup<N, F> = {
    registry: null,
    reading: null,
    label: 'Other identifiers',
    numbers: numbers.filter((number) => !REGISTRY_OF_ID_TYPE.has(number.idType)),
    facts: []
  };
  const otherAnswers: IRegistryGroup<N, F> = {
    registry: null,
    reading: null,
    label: 'Other registry answers',
    numbers: [],
    facts: unread.filter((fact) => !claimed.has(fact.sourceSystem))
  };
  return [...known, ...readings, otherNumbers, otherAnswers].filter(
    (group) => group.numbers.length > 0 || group.facts.length > 0
  );
}
